import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Avatar
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  Payment,
  Today,
  CalendarMonth,
  Print,
  Refresh,
  Search,
  FilterList,
  PictureAsPdf,
  TableChart,
  AccountBalance,
  CreditCard,
  Receipt,
  Visibility,
  ErrorOutline
} from '@mui/icons-material';
import HttpsIcon from '@mui/icons-material/Https';

import { Input, message, ConfigProvider } from 'antd';
import esES from 'antd/es/locale/es_ES';
import moment from 'moment';
import 'moment/locale/es';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { exportDailyPaymentsToPdf } from '../utils/PdfExport';
import { exportDailyPaymentsToExcel } from '../utils/ExcelExport';
import LoadingInformation from '../../Components/LoadingInformation';

moment.locale('es');

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return '0.00';
  }
  return numValue.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

const COLORS = ['#f759ab', '#22c55e', '#3b82f6', '#f97316', '#8b5cf6', '#06b6d4'];

function DailyPaymentsReport() {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('todos');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [permissionError, setPermissionError] = useState(false);

  // Summary metrics
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalAmount: 0,
    averagePayment: 0,
    paymentsToday: 0,
    amountToday: 0,
    paymentsByMethod: {},
    transferAmount: 0,
    cashAmount: 0,
  });

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissionError(true);
      return messageApi.error({
        content: 'No se encontró el token de autenticación. Por favor, vuelve a iniciar sesión.',
        duration: 5
      }).then(() => {
        navigate('/sign-in');
      });
    }
    setPermissionError(false);
    setLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const getPayments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/invoices/list-today-invoices-details`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // data ya es un array plano de pagos
        setPayments(data);
        setFilteredPayments(data);
        calculateSummary(data);
      } else {
        messageApi.error('Error al cargar los pagos');
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (paymentData) => {
    const totalPayments = paymentData.length;
    const totalAmount = paymentData.reduce((sum, payment) => sum + parseNumber(payment.amount), 0);
    const averagePayment = totalPayments > 0 ? totalAmount / totalPayments : 0;

    const today = moment().format('YYYY-MM-DD');
    const paymentsToday = paymentData.filter(payment =>
      moment(payment.creationDate).format('YYYY-MM-DD') === today
    );
    const amountToday = paymentsToday.reduce((sum, payment) => sum + parseNumber(payment.amount), 0);

    // Calcular el monto total por método de pago
    const transferAmount = paymentData
      .filter(payment => (payment.paymentMethod || 'Efectivo').toLowerCase() === 'transferencia')
      .reduce((sum, payment) => sum + parseNumber(payment.amount), 0);

    const cashAmount = paymentData
      .filter(payment => (payment.paymentMethod || 'Efectivo').toLowerCase() !== 'transferencia')
      .reduce((sum, payment) => sum + parseNumber(payment.amount), 0);

    // Agrupar por método de pago
    const paymentsByMethod = {};
    paymentData.forEach(payment => {
      const method = payment.paymentMethod || 'Efectivo';
      if (!paymentsByMethod[method]) {
        paymentsByMethod[method] = { count: 0, amount: 0 };
      }
      paymentsByMethod[method].count++;
      paymentsByMethod[method].amount += parseNumber(payment.amount);
    });

    setSummary({
      totalPayments,
      totalAmount,
      averagePayment,
      paymentsToday: paymentsToday.length,
      amountToday,
      paymentsByMethod,
      transferAmount,
      cashAmount
    });
  };

  const applyFilters = useCallback(() => {
    let filtered = [...payments];

    // Filtrar por método de pago
    if (paymentMethod !== 'todos') {
      filtered = filtered.filter(payment =>
        (payment.paymentMethod || 'Efectivo').toLowerCase() === paymentMethod.toLowerCase()
      );
    }

    // Filtrar por término de búsqueda
    if (search.trim()) {
      const searchTerm = search.toLowerCase();
      filtered = filtered.filter(payment =>
        payment.invoiceNumber?.toString().toLowerCase().includes(searchTerm) ||
        payment.clientName?.toLowerCase().includes(searchTerm) ||
        payment.description?.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredPayments(filtered);
    calculateSummary(filtered);
  }, [payments, search, paymentMethod]);

  const prepareChartData = () => {
    // Datos diarios
    const dailyData = {};
    filteredPayments.forEach(payment => {
      const day = moment(payment.creationDate).format('DD/MM');
      if (!dailyData[day]) {
        dailyData[day] = { day, amount: 0, count: 0 };
      }
      dailyData[day].amount += parseNumber(payment.amount);
      dailyData[day].count += 1;
    });

    // Datos por método de pago
    const methodData = Object.entries(summary.paymentsByMethod).map(([method, data]) => ({
      method,
      amount: data.amount,
      count: data.count,
      color: COLORS[Object.keys(summary.paymentsByMethod).indexOf(method) % COLORS.length]
    }));

    return {
      daily: Object.values(dailyData).sort((a, b) =>
        moment(a.day, 'DD/MM').valueOf() - moment(b.day, 'DD/MM').valueOf()
      ),
      methods: methodData
    };
  };

  const handleExportPDF = () => {
    exportDailyPaymentsToPdf(filteredPayments);
  };

  const handleExportExcel = () => {
    exportDailyPaymentsToExcel(filteredPayments);
  };

  useEffect(() => {
    getPayments();
  }, []);

  useEffect(() => {
    if (payments.length > 0) {
      applyFilters();
    }
  }, [search, paymentMethod, payments, applyFilters]);

  const chartData = prepareChartData();

  if (loading) return <LoadingInformation />;

  // Componente para mostrar cuando no hay datos
  const NoDataComponent = () => (
    <Card sx={{
      borderRadius: 3,
      p: 4,
      textAlign: 'center',
      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
      border: '2px solid #f59e0b'
    }}>
      <ErrorOutline sx={{ fontSize: 80, color: '#f59e0b', mb: 2 }} />
      <Typography variant="h5" sx={{ mb: 2, color: '#92400e', fontWeight: 600 }}>
        No hay pagos registrados hoy
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, color: '#78350f' }}>
        No se encontraron pagos registrados el día de hoy ({moment().format('DD/MM/YYYY')}).
        Los datos se actualizarán automáticamente cuando se registren nuevos pagos.
      </Typography>
      <Button
        variant="contained"
        startIcon={<Refresh />}
        onClick={getPayments}
        sx={{
          backgroundColor: '#f59e0b',
          '&:hover': { backgroundColor: '#d97706' },
          px: 4,
          py: 1
        }}
      >
        Actualizar Datos
      </Button>
    </Card>
  );

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <div style={{ background: '#f3e8ff', minHeight: '100vh', padding: '20px 0' }}>
      <Container maxWidth="xl">
        {contextHolder}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              color: '#4b0082',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' },
              fontSize: {
                xs: '1.5rem',    // h5 size en móviles
                sm: '2.125rem',  // h2 size en tablets
                md: '3rem'       // h2 size en desktop
              }
            }}
          >
            <Payment sx={{ color: '#f759ab', fontSize: { xs: 30, sm: 50 } }} />
            Reporte de Pagos Diarios - {moment().format('DD/MM/YYYY')}
          </Typography>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            {payments.length > 0 && (
              <>
                <Button
                  startIcon={<PictureAsPdf />}
                  onClick={handleExportPDF}
                  variant="contained"
                  sx={{ backgroundColor: '#f759ab', '&:hover': { backgroundColor: '#e04897' } }}
                >
                  Exportar PDF
                </Button>
                <Button
                  startIcon={<TableChart />}
                  onClick={handleExportExcel}
                  variant="contained"
                  sx={{ backgroundColor: '#4b0082', '&:hover': { backgroundColor: '#3730a3' } }}
                >
                  Exportar Excel
                </Button>
              </>
            )}
          </Stack>
        </Box>

        {/* Mostrar mensaje si no hay datos */}
        {payments.length === 0 ? (
          <NoDataComponent />
        ) : (
          <>
            {/* Filters */}
            <Card sx={{ mb: 4, borderRadius: '18px', border: '1.5px solid #f759ab' }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: '#4b0082',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <FilterList sx={{ mr: 1, color: '#f759ab' }} />
                  Filtros de Búsqueda
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <ConfigProvider theme={{ token: { colorPrimary: '#f759ab' } }}>
                      <Input
                        placeholder="Buscar por factura, cliente o descripción"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        size="large"
                        prefix={<Search />}
                      />
                    </ConfigProvider>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: '#4b0082' }}>Método de pago</InputLabel>
                      <Select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        label="Método de pago"
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                        }}
                      >
                        <MenuItem value="todos">Todos</MenuItem>
                        <MenuItem value="efectivo">Efectivo</MenuItem>
                        <MenuItem value="tarjeta">Tarjeta</MenuItem>
                        <MenuItem value="transferencia">Transferencia</MenuItem>
                        <MenuItem value="cheque">Cheque</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, border: '2px solid #22c55e', background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#15803d' }}>
                          {formatCurrency(summary.amountToday)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ingresos de hoy
                        </Typography>
                      </Box>
                      <AttachMoney sx={{ fontSize: 40, color: '#22c55e' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, border: '2px solid #8b5cf6', background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#6b21a8' }}>
                          {summary.paymentsToday}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pagos de hoy
                        </Typography>
                      </Box>
                      <TrendingUp sx={{ fontSize: 40, color: '#8b5cf6' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, border: '2px solid #3b82f6', background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1d4ed8' }}>
                          {formatCurrency(summary.transferAmount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ingresos por Transferencia
                        </Typography>
                      </Box>
                      <AccountBalance sx={{ fontSize: 40, color: '#3b82f6' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, border: '2px solid #f97316', background: 'linear-gradient(135deg, #fed7aa, #fdba74)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#c2410c' }}>
                          {formatCurrency(summary.cashAmount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ingresos por pago en efectivo
                        </Typography>
                      </Box>
                      <CreditCard sx={{ fontSize: 40, color: '#f97316' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                    Ingresos por Hora del Día
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={
                      (() => {
                        const hours = Array.from({ length: 24 }, (_, i) => ({
                          hour: `${i.toString().padStart(2, '0')}:00`,
                          amount: 0,
                          count: 0
                        }));
                        filteredPayments.forEach(payment => {
                          const hour = moment(payment.creationDate).hour();
                          hours[hour].amount += parseNumber(payment.amount);
                          hours[hour].count += 1;
                        });
                        // Solo mostrar horas con movimientos o las últimas 12 horas del día
                        return hours.filter(h => h.amount > 0 || h.count > 0)
                          .length > 0
                          ? hours.filter(h => h.amount > 0 || h.count > 0)
                          : hours.slice(moment().hour() - 11 >= 0 ? moment().hour() - 11 : 0, moment().hour() + 1);
                      })()
                    }>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value, name) => [
                          name === 'amount' ? formatCurrency(value) : value,
                          name === 'amount' ? 'Monto' : 'Cantidad'
                        ]}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="amount"
                        stroke="#f759ab"
                        strokeWidth={3}
                        dot={{ r: 5 }}
                        activeDot={{ r: 8 }}
                        name="Monto"
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#22c55e"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name="Cantidad de Pagos"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                    Métodos de Pago
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData.methods}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ method, percent }) => `${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="amount"
                      >
                        {chartData.methods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Leyenda personalizada */}
                  <Box sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    borderTop: '1px solid #e0e7ff',
                    pt: 2
                  }}>
                    {chartData.methods.map((entry, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              backgroundColor: entry.color,
                              borderRadius: '50%'
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {entry.method.toUpperCase()} ({entry.count})
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{
                          fontWeight: 600,
                          color: '#4b0082',
                          fontSize: '0.85rem'
                        }}>
                          {formatCurrency(entry.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Payments Table */}
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Detalle de Pagos ({filteredPayments.length} registros)
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f0fc' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Factura</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Cliente</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Método</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Monto</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Descripción</TableCell> {/* Nueva columna */}
                        <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Atendido por</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPayments
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((payment, index) => (
                          <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f3e8ff' } }}>
                            <TableCell>
                              {payment.creationDate
                                ? moment(payment.creationDate).format('DD/MM/YYYY HH:mm A')
                                : 'N/A'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#7c3aed' }}>
                              {payment.invoiceNumber}
                            </TableCell>
                            <TableCell>{payment.clientName}</TableCell>
                            <TableCell>
                              <Chip
                                icon={payment.paymentMethod === 'efectivo' ? <AttachMoney /> :
                                  payment.paymentMethod === 'Tarjeta' ? <CreditCard /> :
                                    <AccountBalance />}
                                label={payment.paymentMethod?.toUpperCase() || 'Efectivo'}
                                size="small"
                                variant="outlined"
                                sx={{
                                  color: payment.paymentMethod === 'efectivo' ? '#22c55e' :
                                    payment.paymentMethod === 'Tarjeta' ? '#3b82f6' : '#f97316',
                                  borderColor: payment.paymentMethod === 'efectivo' ? '#22c55e' :
                                    payment.paymentMethod === 'Tarjeta' ? '#3b82f6' : '#f97316'
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#22c55e' }}>
                              {formatCurrency(payment.amount)}
                            </TableCell>
                            <TableCell>
                              {payment.description || '—'}
                            </TableCell>
                            <TableCell>
                              {payment.employeeName || 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Ver factura">
                                <IconButton
                                  onClick={() => navigate(`/billing/invoice/${payment.invoiceID}`)}
                                  sx={{ color: '#f759ab' }}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredPayments.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </div>
  );
}

export default DailyPaymentsReport;